import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FileSignature, Check, X, Clock, Trash2, AlertTriangle, CheckCircle2, XCircle, Plus, ChevronDown, ChevronRight, ExternalLink, HeartPulse, Send, Loader2, PenLine, CircleDollarSign } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import UploadPolicyDialog from "@/components/UploadPolicyDialog";
import PolicyDetailsPreview from "@/components/PolicyDetailsPreview";
import ProvisionReadyBadge from "@/components/ProvisionReadyBadge";
import CustomerStatusBadge from "@/components/CustomerStatusBadge";

// Payment-status palette — mirrors the backend's 5 allowed values. Each
// option carries its own pill colour so the row stays readable at a glance.
const PAYMENT_STATUS_OPTIONS = [
  { v: "wartet_zahlung", label: "Wartet auf Zahlung", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { v: "provvisioniert", label: "Provisioniert",      color: "bg-blue-100 text-blue-800 border-blue-200" },
  { v: "bezahlt",        label: "Bezahlt",            color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { v: "storno",         label: "Storno",             color: "bg-stone-200 text-stone-700 border-stone-300" },
  { v: "abgelehnt",      label: "Abgelehnt",          color: "bg-red-100 text-red-700 border-red-200" },
];
const PAYMENT_STATUS_BY_KEY = Object.fromEntries(PAYMENT_STATUS_OPTIONS.map((o) => [o.v, o]));

function PaymentStatusControl({ antrag, role, onUpdate }) {
  const canEdit = role === "master" || role === "admin" || role === "control";
  const ps = antrag.payment_status || "wartet_zahlung";
  const opt = PAYMENT_STATUS_BY_KEY[ps] || PAYMENT_STATUS_OPTIONS[0];

  // Read-only badge for non-admin roles
  if (!canEdit) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${opt.color}`}
        data-testid={`antrag-payment-status-${antrag.id}`}
        title="Zahlungsstatus (nur Master/Admin können bearbeiten)"
      >
        <CircleDollarSign className="w-3 h-3" />
        {opt.label}
      </span>
    );
  }
  // Editable inline Select for admins
  return (
    <Select
      value={ps}
      onValueChange={async (val) => {
        if (val === ps) return;
        let amount = null;
        if (val === "bezahlt" || val === "provvisioniert") {
          const raw = window.prompt(
            val === "bezahlt"
              ? "Einbezahlter Betrag (CHF) — optional:"
              : "Provisionierter Betrag (CHF) — optional:",
            antrag.paid_amount || antrag.provisioned_amount || ""
          );
          if (raw === null) return; // cancelled
          const n = parseFloat(String(raw).replace(",", "."));
          amount = Number.isFinite(n) ? n : null;
        }
        try {
          const r = await api.patch(`/antraege/${antrag.id}/payment-status`, {
            payment_status: val,
            paid_amount: amount,
          });
          toast.success(`Zahlungsstatus → ${PAYMENT_STATUS_BY_KEY[val]?.label || val}`);
          onUpdate && onUpdate({ ...antrag, ...(r.data?.updated || {}) });
        } catch (e) {
          toast.error(e.response?.data?.detail || "Fehler beim Speichern");
        }
      }}
    >
      <SelectTrigger
        className={`h-6 px-2 py-0 rounded-full text-[10px] font-bold uppercase tracking-widest border ${opt.color} hover:opacity-90 w-auto gap-1`}
        data-testid={`antrag-payment-status-${antrag.id}`}
      >
        <CircleDollarSign className="w-3 h-3" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PAYMENT_STATUS_OPTIONS.map((o) => (
          <SelectItem key={o.v} value={o.v} data-testid={`antrag-payment-status-opt-${o.v}`}>
            <span className="inline-flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${o.color.split(" ")[0]}`} />
              {o.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function StatusBadge({ status, deadline }) {
  if (status === "approved") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-emerald-600 text-white" data-testid="antrag-status-approved">
        <CheckCircle2 className="w-3 h-3" /> Genehmigt
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white">
        <X className="w-3 h-3" /> Abgelehnt
      </span>
    );
  }
  // in_bearbeitung — check overdue
  const overdue = deadline && new Date(deadline) < new Date(new Date().toISOString().slice(0, 10));
  if (overdue) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" data-testid="antrag-status-overdue">
        <AlertTriangle className="w-3 h-3" /> Frist überschritten
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" data-testid="antrag-status-pending">
      <Clock className="w-3 h-3 animate-pulse" /> In Bearbeitung
    </span>
  );
}

function SignatureStatusBadge({ antrag }) {
  const s = antrag?.signature_status;
  if (s === "signed") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" title={antrag.signed_at}>
        <PenLine className="w-3 h-3" /> Signiert
      </span>
    );
  }
  if (s === "pending_signature") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
        <Clock className="w-3 h-3 animate-pulse" /> Wartet Unterschrift
      </span>
    );
  }
  return null;
}

export function AntraegeList({ items, clientId, role, onAdd, onApprove, onReject, onDelete, onUpdate, autoExpandId = null }) {
  const isAdmin = role === "master" || role === "admin" || role === "control";
  const [expandedId, setExpandedId] = useState(null);
  const rowRefs = useRef({});

  // Auto-expand + scroll to a specific antrag when arriving from the Anträge Board with ?antrag=<id>
  useEffect(() => {
    if (!autoExpandId) return;
    const target = (items || []).find((x) => x.id === autoExpandId);
    if (!target) return;
    setExpandedId(autoExpandId);
    // Wait for the row to render, then scroll into view with a soft highlight
    const t = setTimeout(() => {
      const el = rowRefs.current[autoExpandId];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-prime", "ring-offset-2", "ring-offset-background");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-prime", "ring-offset-2", "ring-offset-background");
        }, 2400);
      }
    }, 120);
    return () => clearTimeout(t);
  }, [autoExpandId, items]);
  // Gesundheitsprüfung dialog state
  const [gpDialogAntrag, setGpDialogAntrag] = useState(null);
  const [gpNote, setGpNote] = useState("");
  const [gpSaving, setGpSaving] = useState(false);
  // Signature request state
  const [signLoadingId, setSignLoadingId] = useState(null);
  const [signResult, setSignResult] = useState(null); // {antrag, sign_url, client_email}

  // Generate the signature link + open the Compose dialog (or just copy link)
  const handleRequestSignature = async (antrag) => {
    if (signLoadingId) return;
    setSignLoadingId(antrag.id);
    try {
      const r = await api.post(`/antraege/${antrag.id}/request-signature`);
      const data = r.data;
      if (!data?.sign_url) throw new Error("kein Signatur-Link erhalten");
      toast.success(`Signatur-Link erstellt — gültig bis ${(data.expires_at || '').slice(0,10)}`);
      onUpdate && onUpdate({
        ...antrag,
        signature_status: "pending_signature",
        signature_request_token: data.token,
        signature_requested_at: new Date().toISOString(),
      });
      setSignResult({ antrag, ...data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signatur-Link konnte nicht erstellt werden");
    } finally {
      setSignLoadingId(null);
    }
  };

  const copySignUrl = async () => {
    try {
      await navigator.clipboard.writeText(signResult?.sign_url || "");
      toast.success("Link kopiert");
    } catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  const handleGpToggle = async (antrag) => {
    if (antrag.gesundheitspruefung_required) {
      // Already ON → confirm deactivation directly
      if (!window.confirm("Gesundheitsprüfung deaktivieren und Frist auf 14 Tage zurücksetzen?")) return;
      try {
        const r = await api.patch(`/antraege/${antrag.id}/gesundheitspruefung`, { required: false });
        toast.success("Gesundheitsprüfung deaktiviert · Frist zurückgesetzt");
        onUpdate && onUpdate(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Fehler");
      }
    } else {
      // Open dialog for note
      setGpDialogAntrag(antrag);
      setGpNote("");
    }
  };

  const confirmGpActivation = async () => {
    if (!gpDialogAntrag) return;
    setGpSaving(true);
    try {
      const r = await api.patch(`/antraege/${gpDialogAntrag.id}/gesundheitspruefung`, {
        required: true,
        note: gpNote.trim() || null,
      });
      toast.success("Gesundheitsprüfung aktiviert · Frist um 4 Wochen verlängert");
      onUpdate && onUpdate(r.data);
      setGpDialogAntrag(null);
      setGpNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setGpSaving(false);
    }
  };

  const openAntragFile = async (antragId) => {
    try {
      const r = await api.get(`/antraege/${antragId}/document`);
      const doc = r.data;
      const token = localStorage.getItem("crm_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Document not accessible");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kein Dokument vorhanden");
    }
  };

  return (
    <div data-testid="antraege-list">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border">
        <div className="flex items-center gap-2 flex-wrap">
          <FileSignature className="w-4 h-4 text-prime" strokeWidth={1.7} />
          <span className="text-sm font-medium">Anträge in Bearbeitung</span>
          <span className="text-xs text-muted-foreground">— Frist: 14 Tage</span>
          <span className="text-[10px] text-muted-foreground/70 italic flex items-center gap-1">
            <HeartPulse className="w-3 h-3" /> +4 Wochen bei Gesundheitsprüfung
          </span>
        </div>
        <Button size="sm" className="btn-prime" onClick={onAdd} data-testid="new-antrag-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Neuer Antrag
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground">Keine Anträge vorhanden.</div>
      ) : (
        <div className="divide-y divide-border">
          {items.map((a) => {
            const overdue = a.status === "in_bearbeitung" && a.deadline && new Date(a.deadline) < new Date(new Date().toISOString().slice(0, 10));
            const expanded = expandedId === a.id;
            const hasDetails = a.details && typeof a.details === "object" && Object.keys(a.details).length > 0;
            return (
              <div
                key={a.id}
                ref={(el) => { if (el) rowRefs.current[a.id] = el; }}
                className={`transition-shadow duration-300 rounded-md ${a.status === "approved" ? "bg-emerald-50/40 dark:bg-emerald-950/10" : overdue ? "bg-red-50/40 dark:bg-red-950/10" : ""}`}
                data-testid={`antrag-row-${a.id}`}
              >
                <div className="p-4 sm:p-5 flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-md border flex items-center justify-center shrink-0 ${
                    a.status === "approved" ? "border-emerald-500/60 text-emerald-600 bg-card"
                    : a.status === "rejected" ? "border-red-500/60 text-red-600 bg-card"
                    : overdue ? "border-red-500/60 text-red-600 bg-card"
                    : "border-amber-500/60 text-amber-600 bg-card"
                  }`}>
                    <FileSignature className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <button
                    onClick={() => setExpandedId(expanded ? null : a.id)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`expand-antrag-${a.id}`}
                    title="Riassunto Antrag anzeigen"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {expanded
                        ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground inline" />
                        : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground inline" />}
                      <span className="font-semibold text-sm hover:text-prime">{a.company}</span>
                      <span className="text-xs text-muted-foreground">— {a.insurance_type}</span>
                      <CustomerStatusBadge status={a.customer_status} />
                      <StatusBadge status={a.status} deadline={a.deadline} />
                      <SignatureStatusBadge antrag={a} />
                      {a.status === "approved" && (
                        <PaymentStatusControl antrag={a} role={role} onUpdate={onUpdate} />
                      )}
                      <ProvisionReadyBadge insuranceType={a.insurance_type} premium={a.expected_premium} details={a.details} testId={a.id} />
                      {a.gesundheitspruefung_required && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                          data-testid={`antrag-gp-badge-${a.id}`}
                          title={a.gesundheitspruefung_note || "Gesundheitsprüfung von der Versicherung angefragt"}
                        >
                          <HeartPulse className="w-3 h-3" /> Gesundheitsprüfung · +4 Wochen
                        </span>
                      )}
                    </div>
                    {a.policy_number && (
                      <div className="text-xs font-mono mt-1">
                        <span className="text-muted-foreground">Policen-Nr.: </span>
                        <span className="font-bold text-emerald-700 dark:text-emerald-400">{a.policy_number}</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1 space-x-3">
                      <span>Erwartete Prämie: <span className="font-mono">{a.currency} {Number(a.expected_premium || 0).toLocaleString("de-CH")}</span></span>
                      {a.start_date && <span>Beginn: <span className="font-mono">{fmtDate(a.start_date)}</span></span>}
                      {a.end_date && <span>Ablauf: <span className="font-mono">{fmtDate(a.end_date)}</span></span>}
                      <span>Eingereicht: {fmtDate(a.created_at)}</span>
                      {a.status === "in_bearbeitung" && (
                        <span className={overdue ? "text-red-600 font-bold" : "text-amber-700"}>
                          Frist: {fmtDate(a.deadline)}
                        </span>
                      )}
                    </div>
                    {a.notes && (
                      <div className="text-xs text-muted-foreground mt-1 italic">{a.notes}</div>
                    )}
                    {a.rejection_reason && (
                      <div className="text-xs text-red-600 mt-1">Ablehnungsgrund: {a.rejection_reason}</div>
                    )}
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => { e.stopPropagation(); openAntragFile(a.id); }}
                      data-testid={`open-antrag-${a.id}`}
                      title="Antrag-Datei öffnen"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && a.status === "in_bearbeitung" && a.signature_status !== "signed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className={`h-8 px-2 gap-1 ${a.signature_status === "pending_signature"
                          ? "border-amber-300 text-amber-700 bg-amber-50/60 hover:bg-amber-50"
                          : "border-prime/40 text-prime hover:bg-prime/10"
                        }`}
                        onClick={(e) => { e.stopPropagation(); handleRequestSignature(a); }}
                        disabled={signLoadingId === a.id}
                        title={a.signature_status === "pending_signature"
                          ? "Neuen Signatur-Link erstellen · alter bleibt gültig"
                          : "Dokument zur Unterschrift an Kunde senden"}
                        data-testid={`request-signature-${a.id}`}
                      >
                        {signLoadingId === a.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <PenLine className="w-3.5 h-3.5" />}
                        <span className="text-[11px] hidden md:inline">
                          {a.signature_status === "pending_signature" ? "Erneut senden" : "Zur Unterschrift"}
                        </span>
                      </Button>
                    )}
                    {isAdmin && a.status === "in_bearbeitung" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className={`h-8 px-2 gap-1 ${a.gesundheitspruefung_required
                            ? "border-rose-400 text-rose-700 hover:bg-rose-50 bg-rose-50/50"
                            : "border-stone-300 text-stone-600 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                          }`}
                          onClick={(e) => { e.stopPropagation(); handleGpToggle(a); }}
                          data-testid={`gp-toggle-${a.id}`}
                          title={a.gesundheitspruefung_required
                            ? "Gesundheitsprüfung deaktivieren · Frist auf 14 Tage zurücksetzen"
                            : "Gesundheitsprüfung anfragen · Frist um +4 Wochen verlängern"}
                        >
                          <HeartPulse className="w-3.5 h-3.5" />
                          <span className="text-[11px] hidden md:inline">{a.gesundheitspruefung_required ? "GP aktiv" : "GP"}</span>
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                          onClick={() => onApprove(a)}
                          data-testid={`approve-antrag-${a.id}`}
                        >
                          <Check className="w-4 h-4" /> Genehmigen
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 gap-1.5"
                          onClick={() => onReject && onReject(a)}
                          data-testid={`reject-antrag-${a.id}`}
                        >
                          <XCircle className="w-4 h-4" /> Ablehnen
                        </Button>
                      </>
                    )}
                    <button onClick={() => onDelete(a)} className="text-muted-foreground hover:text-red-600 p-2" data-testid={`delete-antrag-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {expanded && (
                  <div className="px-4 sm:px-6 pb-4 pl-16 sm:pl-20 -mt-2" data-testid={`antrag-details-${a.id}`}>
                    {hasDetails ? (
                      <div className="rounded-md border border-border bg-card/50 p-3">
                        <div className="label-uppercase text-muted-foreground mb-2">Riassunto Antrag</div>
                        <PolicyDetailsPreview details={a.details} type={a.insurance_type} />
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed border-border bg-secondary/40 p-3 text-xs text-muted-foreground italic">
                        Keine weiteren Angaben — der Berater hat nur die Basisinformationen erfasst.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Signature link ready dialog — shows the URL with a Copy action */}
      <Dialog open={!!signResult} onOpenChange={(o) => { if (!o) setSignResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="sign-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-prime" />
              Signatur-Link bereit
            </DialogTitle>
            <DialogDescription>
              Der Link unten ist <b>14 Tage</b> gültig. Senden Sie ihn an den Kunden — sobald er unterschreibt,
              wird der Antrag automatisch als <span className="text-emerald-700 font-semibold">Signiert</span> markiert
              und das unterzeichnete PDF im Kundendossier abgelegt.
            </DialogDescription>
          </DialogHeader>
          {signResult && (
            <div className="space-y-3">
              <div className="bg-secondary/40 border border-border rounded p-3 text-xs space-y-1">
                <div><span className="text-muted-foreground">Kunde:</span> <b>{signResult.client_name}</b></div>
                {signResult.client_email && (
                  <div><span className="text-muted-foreground">E-Mail:</span> <span className="font-mono">{signResult.client_email}</span></div>
                )}
                <div><span className="text-muted-foreground">Antrag:</span> {signResult.company} — {signResult.insurance_type}</div>
                <div><span className="text-muted-foreground">Gültig bis:</span> <b>{(signResult.expires_at || '').slice(0,10)}</b></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Signatur-Link</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={signResult.sign_url} readOnly className="font-mono text-xs" data-testid="sign-link-url" />
                  <Button onClick={copySignUrl} variant="outline" size="sm" data-testid="copy-sign-link">Kopieren</Button>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setSignResult(null); }}
                  data-testid="sign-link-close"
                >
                  Schliessen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gesundheitsprüfung confirmation dialog */}
      <Dialog open={!!gpDialogAntrag} onOpenChange={(o) => { if (!o) { setGpDialogAntrag(null); setGpNote(""); } }}>
        <DialogContent data-testid="gp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-rose-600" />
              Gesundheitsprüfung anfragen
            </DialogTitle>
            <DialogDescription>
              Die Versicherung verlangt eine zusätzliche Gesundheitsprüfung (z.B. ärztliche Kontrolle, Nachfrage zum Gesundheitsfragebogen).
              Die Frist wird automatisch um <b>+4 Wochen (28 Tage)</b> verlängert.
            </DialogDescription>
          </DialogHeader>
          {gpDialogAntrag && (
            <div className="space-y-3 py-2">
              <div className="rounded-md bg-stone-50 border border-stone-200 px-3 py-2 text-xs">
                <div><span className="text-stone-500">Antrag:</span> <b>{gpDialogAntrag.company}</b> · {gpDialogAntrag.insurance_type}</div>
                <div><span className="text-stone-500">Aktuelle Frist:</span> {fmtDate(gpDialogAntrag.deadline)}</div>
              </div>
              <div>
                <Label className="text-xs">Hinweis der Versicherung (optional)</Label>
                <Textarea
                  value={gpNote}
                  onChange={(e) => setGpNote(e.target.value)}
                  placeholder="z.B. Arztbesuch erforderlich, Gesundheitsfragebogen unvollständig…"
                  rows={3}
                  data-testid="gp-note-textarea"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setGpDialogAntrag(null); setGpNote(""); }} data-testid="gp-cancel">
              Abbrechen
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
              onClick={confirmGpActivation}
              disabled={gpSaving}
              data-testid="gp-confirm"
            >
              <HeartPulse className="w-4 h-4" />
              {gpSaving ? "Speichern…" : "Anfragen · +4 Wochen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AntragDialog({ open, onOpenChange, clientId, onCreated, clientFirstName = "", clientLastName = "" }) {
  // Use the same Police/Vertrag upload dialog (with AI extraction + type-specific
  // details), but in "antrag" mode so it POSTs to /api/antraege and treats the
  // policy_number field as optional.
  return (
    <UploadPolicyDialog
      open={open}
      onOpenChange={onOpenChange}
      clientId={clientId}
      onCreated={onCreated}
      mode="antrag"
      clientFirstName={clientFirstName}
      clientLastName={clientLastName}
    />
  );
}

export function ApproveAntragDialog({ antrag, onOpenChange, onApproved }) {
  if (!antrag) return null;
  return (
    <ApproveAntragDialogInner
      key={antrag.id}
      antrag={antrag}
      onOpenChange={onOpenChange}
      onApproved={onApproved}
    />
  );
}

function ApproveAntragDialogInner({ antrag, onOpenChange, onApproved }) {
  const [form, setForm] = useState(() => ({
    policy_number: antrag?.policy_number || "",
    start_date: antrag?.start_date || new Date().toISOString().slice(0, 10),
    end_date: antrag?.end_date || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10),
    premium: antrag?.expected_premium || 0,
  }));
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    if (!form.policy_number.trim()) { toast.error("Policen-Nummer erforderlich"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/antraege/${antrag.id}/approve`, {
        policy_number: form.policy_number,
        start_date: form.start_date,
        end_date: form.end_date,
        premium: parseFloat(form.premium) || undefined,
      });
      toast.success("Antrag genehmigt — Police automatisch erstellt");
      onApproved && onApproved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  if (!antrag) return null;
  return (
    <Dialog open={!!antrag} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Antrag genehmigen</DialogTitle>
          <DialogDescription>
            {antrag.company} · {antrag.insurance_type} — Geben Sie die offizielle Policen-Nummer der Gesellschaft ein.
            {(antrag.start_date || antrag.end_date) && (
              <span className="block mt-1 text-[11px] text-prime">
                Vorausgefüllte Daten aus dem Antrag · überprüfen Sie diese vor der Genehmigung.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-uppercase text-muted-foreground">Policen-Nr.</Label>
            <Input
              value={form.policy_number}
              onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
              placeholder="z.B. POL-12345-2026"
              className="mt-1.5 h-10"
              data-testid="approve-policy-nr"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase text-muted-foreground">Startdatum</Label>
              <Input type="date" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="mt-1.5 h-10" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">Ablaufdatum</Label>
              <Input type="date" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="mt-1.5 h-10" />
            </div>
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Prämie ({antrag.currency})</Label>
            <Input type="number" step="0.01" value={form.premium}
              onChange={(e) => setForm({ ...form, premium: e.target.value })}
              className="mt-1.5 h-10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="btn-prime" onClick={approve} disabled={busy} data-testid="confirm-approve-antrag">
            <Check className="w-4 h-4 mr-1.5" /> Genehmigen & Police anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RejectAntragDialog({ antrag, onOpenChange, onRejected }) {
  if (!antrag) return null;
  return (
    <RejectAntragDialogInner
      key={antrag.id}
      antrag={antrag}
      onOpenChange={onOpenChange}
      onRejected={onRejected}
    />
  );
}

function RejectAntragDialogInner({ antrag, onOpenChange, onRejected }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const reject = async () => {
    if (!reason.trim()) {
      toast.error("Bitte geben Sie einen Ablehnungsgrund an");
      return;
    }
    setBusy(true);
    try {
      const r = await api.patch(`/antraege/${antrag.id}/reject`, null, {
        params: { reason: reason.trim() },
      });
      toast.success("Antrag abgelehnt");
      onRejected && onRejected(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ablehnung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="label-uppercase text-red-600">Antrag ablehnen</div>
          <DialogTitle className="font-display text-2xl">
            {antrag.company} · {antrag.insurance_type}
          </DialogTitle>
          <DialogDescription>
            Antrag-Nr. {antrag.policy_number || "—"} · Versicherer hat die Police nicht ausgestellt oder es liegen fehlende Unterlagen vor.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-uppercase text-muted-foreground">Grund der Ablehnung *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Antrag von der Gesellschaft abgelehnt wegen Vorerkrankung · Wartefrist · fehlende Unterlagen…"
              rows={4}
              className="mt-1.5 resize-none"
              data-testid="reject-antrag-reason"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Dieser Grund wird im Dossier hinterlegt und an den verantwortlichen Berater per Notification mitgeteilt.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={reject}
            disabled={busy || !reason.trim()}
            data-testid="confirm-reject-antrag"
          >
            <XCircle className="w-4 h-4 mr-1.5" /> Definitiv ablehnen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

